import {
    DrugTagPreparationFateDTO,
    DrugTagPrescriptionReportIndicatorsDTO,
} from '../../dto/DrugTagPrescriptionReportDTO';
import { PREPARATION_FATE_LABELS } from './preparationFate';

const integer = new Intl.NumberFormat('es-AR');
const percent = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** Unidades de stock: puede haber fracciones. */
const units = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

interface FigureProps {
    label: string;
    value: number;
    detail?: string;
}

/** Una cifra: etiqueta, valor destacado y, si hace falta, qué cuenta. */
function Figure({ label, value, detail }: FigureProps) {
    return (
        <div className="nv-ro">
            <dt className="nv-ro-label">{label}</dt>
            <dd className="nv-kpi">{integer.format(value)}</dd>
            {detail && <dd className="nv-help">{detail}</dd>}
        </div>
    );
}

/** Fila del total, debajo de las partes que lo suman. */
function Total({ label, value }: { label: string; value: number }) {
    return (
        <>
            <hr className="nv-divider" />
            <div className="nv-summary-total">
                <span className="nv-section-title">{label}</span>
                <span className="nv-kpi">{integer.format(value)}</span>
            </div>
        </>
    );
}

/** "12,5 % · Es pérdida": qué parte de lo preparado sin aplicar es y qué quiere decir. */
function fateDetail(fate: DrugTagPreparationFateDTO, meaning: string) {
    return fate.percent === null ? meaning : `${percent.format(fate.percent)} % · ${meaning}`;
}

/** La línea de los descartes que no están en la tarjeta, en singular o plural. */
function otherDiscardsText(count: number) {
    return count === 1
        ? 'Además se descartó 1 preparación de una dosis que no está en esta tarjeta: se aplicó, se volvió a preparar o sigue pendiente.'
        : `Además se descartaron ${integer.format(count)} preparaciones de dosis que no están en esta tarjeta: se`
            + ' aplicaron, se volvieron a preparar o siguen pendientes.';
}

/**
 * Lo que volvió a farmacia sin decir de qué dosis viene: una nota, no un destino, porque son unidades y no dosis.
 */
function recoveredText(count: number) {
    return `En el período ${count === 1 ? 'volvió' : 'volvieron'} a farmacia ${units.format(count)}`
        + ` ${count === 1 ? 'unidad' : 'unidades'} de estos medicamentos por Recupero de Preparaciones. Ese recupero no dice`
        + ' de qué dosis viene: parte de lo que figura sin devolución puede haber vuelto así.';
}

/**
 * Resumen de todas las dosis de la consulta (no solo de las que cargó el scroll, y sin el filtro de aplicación, que
 * cambia solo la grilla). Cada card muestra partes que suman su total: las prescriptas se reparten en aplicadas, no
 * aplicadas, suspendidas y pendientes; lo preparado sin aplicar sale de las no aplicadas y de las suspendidas, y se
 * reparte también en sus cuatro destinos.
 */
export default function DrugTagReportIndicators({ indicators }: { indicators: DrugTagPrescriptionReportIndicatorsDTO }) {
    const compliance = indicators.compliancePercent === null
        ? 'Todavía no correspondía aplicar ninguna'
        : `${percent.format(indicators.compliancePercent)} % de lo que correspondía`;
    const excluded = indicators.appliedAsNeeded > 0
        ? `las suspendidas, las pendientes ni las ${integer.format(indicators.appliedAsNeeded)} aplicadas según necesidad`
        : 'las suspendidas ni las pendientes';

    return (
        <>
            <section className="nv-card" aria-labelledby="drug-tag-report-indicators">
                <h2 className="nv-section-title" id="drug-tag-report-indicators">Resumen</h2>
                <dl className="nv-summary-grid">
                    <Figure label="Aplicadas" value={indicators.applied} detail={compliance} />
                    <Figure label="No aplicadas" value={indicators.notApplied} detail="Vencidas o atrasadas" />
                    <Figure label="Suspendidas" value={indicators.suspended} detail="Se suspendió la prescripción" />
                    <Figure label="Pendientes" value={indicators.pending} detail="Todavía no vencieron" />
                </dl>
                <Total label="Prescriptas" value={indicators.prescribed} />
                <p className="nv-help">
                    <i className="pi pi-info-circle" aria-hidden="true" /> El % no incluye {excluded}.
                </p>
            </section>

            <section className="nv-card" aria-labelledby="drug-tag-report-prepared">
                <h2 className="nv-section-title" id="drug-tag-report-prepared">Preparado sin aplicar</h2>
                <dl className="nv-summary-grid">
                    <Figure label="De las no aplicadas" value={indicators.preparedAmongNotApplied} />
                    <Figure label="De las suspendidas" value={indicators.preparedAmongSuspended} />
                </dl>
                <Total label="Preparadas sin aplicar" value={indicators.preparedNotApplied} />
                <p className="nv-help">
                    <i className="pi pi-info-circle" aria-hidden="true" /> Dosis a las que se les preparó una
                    presentación y no se aplicaron, incluidas las descartadas (se cuentan desde que el sistema las guarda).
                    Lo que se retornó al stock no cuenta. Ya están contadas en No aplicadas y Suspendidas.
                </p>
                {indicators.preparedNotApplied > 0 && (
                    <>
                        <hr className="nv-divider" />
                        <h3 className="nv-strong" id="drug-tag-report-fate">Qué pasó con lo preparado</h3>
                        <dl className="nv-summary-grid" aria-labelledby="drug-tag-report-fate">
                            <Figure label={PREPARATION_FATE_LABELS.DISCARDED} value={indicators.discarded.doses}
                                detail={fateDetail(indicators.discarded, 'Es pérdida')} />
                            <Figure label={PREPARATION_FATE_LABELS.RETURNED_PREPARED}
                                value={indicators.returnedPrepared.doses}
                                detail={fateDetail(indicators.returnedPrepared, 'Está en farmacia, preparada')} />
                            <Figure label={PREPARATION_FATE_LABELS.NEVER_DELIVERED} value={indicators.neverDelivered.doses}
                                detail={fateDetail(indicators.neverDelivered, 'Nunca llegó a Enfermería')} />
                            <Figure label={PREPARATION_FATE_LABELS.NOT_RETURNED} value={indicators.notReturned.doses}
                                detail={fateDetail(indicators.notReturned, 'Puede haber vuelto por recupero')} />
                        </dl>
                    </>
                )}
                {indicators.preparedNotApplied > 0 && indicators.recoveredUnits !== null && indicators.recoveredUnits > 0 && (
                    <p className="nv-help">
                        <i className="pi pi-info-circle" aria-hidden="true" />{' '}
                        {recoveredText(indicators.recoveredUnits)}
                    </p>
                )}
                {indicators.discardedInOtherDoses > 0 && (
                    <p className="nv-help">
                        <i className="pi pi-info-circle" aria-hidden="true" />{' '}
                        {otherDiscardsText(indicators.discardedInOtherDoses)}
                    </p>
                )}
            </section>
        </>
    );
}
